import React, { createContext, useEffect, useMemo, useRef, useState } from "react";
import { ProgressIndicatorOverlay } from "../components/progress-indicator";
import { useIsDesktop } from "../hooks/context-provider-hooks/use-is-desktop";
import { ProgressIndicatorOverlayContextProviderProps, ProgressIndicatorOverlayStateSetters } from "./types";

export const ProgressIndicatorOverlayStateContext = createContext<boolean>(undefined);
ProgressIndicatorOverlayStateContext.displayName = "ProgressIndicatorOverlayStateContext";

export const ProgressIndicatorOverlayStateSetterContext =
  createContext<ProgressIndicatorOverlayStateSetters>(undefined);
ProgressIndicatorOverlayStateSetterContext.displayName = "ProgressIndicatorOverlayStateSetterContext";

/**
 * A context provider that provides a progress indicator overlay UI to be triggered from anywhere within its children
 * sub-tree using convenience functions. The internal loading state that this context provider maintains and the
 * convenience functions to update this state to show/hide the overlay can be access via the following hooks.
 *
 * ### `useProgressIndicatorOverlayLoading`
 *
 * This hook returns the current `loading` state value from the context provider.
 *
 * ### `useProgressIndicatorOverlay`
 *
 * This hook return three convenience functions:
 *
 * - `startLoading`
 * - `endLoading`
 * - `resetLoading`
 *
 * These can be used to show/hide the progress indicator overlay.
 *
 * Note that this context provider internally uses the `ScreenSizeContextProvider` to render appropriate UI for mobile
 * and desktop screen sizes. If it is not used within the `ScreenSizeContextProvider` then it defaults to rendering the
 * mobile view of the progress indicator overlay. This behavior can also be overridden using its `isDesktop` prop, which
 * takes precedence over the value obtained from the `ScreenSizeContextProvider`.
 *
 * This context provider accepts optional `label` and `labelPlacement` props to allow showing a label next to the
 * progress indicator spinner. A label can also be passed to the `startLoading` function, that overrides the value
 * passed as the context provider prop.
 */
export const ProgressIndicatorOverlayContextProvider = ({
  label,
  labelPlacement,
  isDesktop: isDesktopProp,
  children,
}: ProgressIndicatorOverlayContextProviderProps) => {
  const isDesktop = useIsDesktop(true);
  const defaultLabel = useRef(label);

  const [state, setState] = useState({
    label,
    loadingCounter: 0,
  });

  useEffect(() => {
    setState((state) => {
      if (label !== state.label) {
        defaultLabel.current = label;
        return { ...state, label };
      }
      return state;
    });
  }, [label]);

  const stateSetters = useMemo<ProgressIndicatorOverlayStateSetters>(
    () => ({
      startLoading: (maybeLabel = defaultLabel.current) => {
        setState((state) => ({
          ...state,
          label: maybeLabel,
          loadingCounter: state.loadingCounter + 1,
        }));
      },
      endLoading: () => {
        setState((state) => ({ ...state, loadingCounter: Math.max(state.loadingCounter - 1, 0) }));
      },
      resetLoading: () => {
        setState((state) => ({ ...state, loadingCounter: 0 }));
      },
    }),
    []
  );

  const loading = state.loadingCounter > 0;

  return (
    <ProgressIndicatorOverlayStateContext.Provider value={loading}>
      <ProgressIndicatorOverlayStateSetterContext.Provider value={stateSetters}>
        {children}
        <ProgressIndicatorOverlay
          open={loading}
          label={state.label}
          labelPlacement={labelPlacement}
          size={isDesktopProp ?? isDesktop ? "large" : "medium"}
          color="white"
        />
      </ProgressIndicatorOverlayStateSetterContext.Provider>
    </ProgressIndicatorOverlayStateContext.Provider>
  );
};
